export const DEFAULT_REPORT_CRON = '0 0 0 1 * ?';

export const CronEditorMode = Object.freeze({
    SIMPLE: 'simple',
    ADVANCED: 'advanced',
});

export const CronSimpleType = Object.freeze({
    EVERY_MINUTES: 'everyMinutes',
    DAILY: 'daily',
    WEEKDAYS: 'weekdays',
    WEEKLY: 'weekly',
    MONTHLY: 'monthly',
});

export const CRON_DAY_OPTIONS = [
    { value: 'MON', label: 'Pon' },
    { value: 'TUE', label: 'Wt' },
    { value: 'WED', label: 'Śr' },
    { value: 'THU', label: 'Czw' },
    { value: 'FRI', label: 'Pt' },
    { value: 'SAT', label: 'Sob' },
    { value: 'SUN', label: 'Niedz' },
];

const DAY_LABELS = CRON_DAY_OPTIONS.reduce((acc, item) => {
    acc[item.value] = item.label;
    return acc;
}, {});

const DAY_ORDER = CRON_DAY_OPTIONS.reduce((acc, item, index) => {
    acc[item.value] = index;
    return acc;
}, {});

const SIMPLE_DEFAULTS = Object.freeze({
    type: CronSimpleType.MONTHLY,
    intervalMinutes: 5,
    hour: 0,
    minute: 0,
    daysOfWeek: ['MON'],
    dayOfMonth: 1,
});

const normalizeWhitespace = (value) => (value || '').trim().replace(/\s+/g, ' ');

export const splitQuartzCron = (cron) => {
    const normalized = normalizeWhitespace(cron);
    if (!normalized) return null;
    const parts = normalized.split(' ');
    return parts.length >= 6 && parts.length <= 7 ? parts : null;
}

const isIntegerInRange = (value, min, max) => {
    if (!/^\d+$/.test(String(value))) return false;
    const number = Number(value);
    return number >= min && number <= max;
}

const isSupportedSecondToken = (value) => value === '0' || value === '*';

const sortDays = (days) => {
    return [...days]
        .filter(day => DAY_ORDER[day] != null)
        .sort((a, b) => DAY_ORDER[a] - DAY_ORDER[b]);
}

const normalizeDayList = (value) => {
    if (!value || value === '*' || value === '?') return [];
    const days = value.split(',').map(item => item.trim().toUpperCase()).filter(Boolean);
    if (days.length === 0) return [];
    const unique = Array.from(new Set(days));
    if (unique.some(day => DAY_ORDER[day] == null)) return [];
    return sortDays(unique);
}

const parseEveryMinutes = ({ second, minute, hour, dayOfMonth, month, dayOfWeek, year }) => {
    if (year && year !== '*') return null;
    if (!isSupportedSecondToken(second)) return null;
    if (hour !== '*' || dayOfMonth !== '?' || month !== '*' || dayOfWeek !== '*') return null;
    if (minute === '*') {
        return { ...SIMPLE_DEFAULTS, type: CronSimpleType.EVERY_MINUTES, intervalMinutes: 1 };
    }
    const intervalMatch = minute.match(/^0\/(\d{1,2})$/);
    if (!intervalMatch) return null;
    const interval = Number(intervalMatch[1]);
    if (interval < 1 || interval > 59) return null;
    return { ...SIMPLE_DEFAULTS, type: CronSimpleType.EVERY_MINUTES, intervalMinutes: interval };
}

const parseTimedSchedule = ({ second, minute, hour, dayOfMonth, month, dayOfWeek, year }) => {
    if (year && year !== '*') return null;
    if (!isSupportedSecondToken(second) || month !== '*') return null;
    if (!isIntegerInRange(minute, 0, 59) || !isIntegerInRange(hour, 0, 23)) return null;

    const base = {
        ...SIMPLE_DEFAULTS,
        hour: Number(hour),
        minute: Number(minute),
    };

    if (dayOfMonth === '?' && dayOfWeek === '*') {
        return { ...base, type: CronSimpleType.DAILY };
    }
    if (dayOfMonth === '?' && dayOfWeek === 'MON-FRI') {
        return { ...base, type: CronSimpleType.WEEKDAYS };
    }
    if (dayOfMonth === '?' && dayOfWeek.includes(',')) {
        const days = normalizeDayList(dayOfWeek);
        if (days.length === 0) return null;
        return { ...base, type: CronSimpleType.WEEKLY, daysOfWeek: days };
    }
    if (dayOfMonth === '?' && DAY_ORDER[dayOfWeek] != null) {
        return { ...base, type: CronSimpleType.WEEKLY, daysOfWeek: [dayOfWeek] };
    }
    if (dayOfWeek === '?' && isIntegerInRange(dayOfMonth, 1, 31)) {
        return { ...base, type: CronSimpleType.MONTHLY, dayOfMonth: Number(dayOfMonth) };
    }
    return null;
}

export const parseQuartzCronToSimpleModel = (cron) => {
    const parts = splitQuartzCron(cron);
    if (!parts) return null;
    const [second, minute, hour, dayOfMonth, month, dayOfWeek, year = null] = parts;
    const normalized = { second, minute, hour, dayOfMonth, month, dayOfWeek, year };
    return parseEveryMinutes(normalized) || parseTimedSchedule(normalized);
}

const clamp = (value, min, max, fallback) => {
    const number = Number(value);
    if (Number.isNaN(number)) return fallback;
    return Math.min(max, Math.max(min, number));
}

export const createDefaultSimpleCronModel = (overrides = {}) => ({
    ...SIMPLE_DEFAULTS,
    ...overrides,
});

export const normalizeSimpleCronModel = (model) => {
    const type = Object.values(CronSimpleType).includes(model && model.type)
        ? model.type
        : SIMPLE_DEFAULTS.type;
    const days = sortDays((model && Array.isArray(model.daysOfWeek) ? model.daysOfWeek : SIMPLE_DEFAULTS.daysOfWeek)
        .filter(day => DAY_ORDER[day] != null));

    return {
        type,
        intervalMinutes: clamp(model && model.intervalMinutes, 1, 59, SIMPLE_DEFAULTS.intervalMinutes),
        hour: clamp(model && model.hour, 0, 23, SIMPLE_DEFAULTS.hour),
        minute: clamp(model && model.minute, 0, 59, SIMPLE_DEFAULTS.minute),
        dayOfMonth: clamp(model && model.dayOfMonth, 1, 31, SIMPLE_DEFAULTS.dayOfMonth),
        daysOfWeek: days.length > 0 ? days : SIMPLE_DEFAULTS.daysOfWeek,
    };
}

export const buildQuartzCronFromSimpleModel = (model) => {
    const normalized = normalizeSimpleCronModel(model);
    switch (normalized.type) {
        case CronSimpleType.EVERY_MINUTES:
            return normalized.intervalMinutes === 1
                ? '0 * * ? * *'
                : `0 0/${normalized.intervalMinutes} * ? * *`;
        case CronSimpleType.DAILY:
            return `0 ${normalized.minute} ${normalized.hour} ? * *`;
        case CronSimpleType.WEEKDAYS:
            return `0 ${normalized.minute} ${normalized.hour} ? * MON-FRI`;
        case CronSimpleType.WEEKLY:
            return `0 ${normalized.minute} ${normalized.hour} ? * ${normalized.daysOfWeek.join(',')}`;
        case CronSimpleType.MONTHLY:
        default:
            return `0 ${normalized.minute} ${normalized.hour} ${normalized.dayOfMonth} * ?`;
    }
}

const formatTime = (hour, minute) => `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

export const describeSimpleCronModel = (model) => {
    const normalized = normalizeSimpleCronModel(model);
    switch (normalized.type) {
        case CronSimpleType.EVERY_MINUTES:
            return normalized.intervalMinutes === 1
                ? 'Co minutę'
                : `Co ${normalized.intervalMinutes} minut`;
        case CronSimpleType.DAILY:
            return `Codziennie o ${formatTime(normalized.hour, normalized.minute)}`;
        case CronSimpleType.WEEKDAYS:
            return `W dni robocze o ${formatTime(normalized.hour, normalized.minute)}`;
        case CronSimpleType.WEEKLY: {
            const labels = normalized.daysOfWeek.map(day => DAY_LABELS[day]).filter(Boolean);
            return `W ${labels.join(', ')} o ${formatTime(normalized.hour, normalized.minute)}`;
        }
        case CronSimpleType.MONTHLY:
        default:
            return `${normalized.dayOfMonth}. dnia każdego miesiąca o ${formatTime(normalized.hour, normalized.minute)}`;
    }
}

export const describeQuartzCron = (cron) => {
    const parsed = parseQuartzCronToSimpleModel(cron);
    if (parsed) return describeSimpleCronModel(parsed);
    return normalizeWhitespace(cron) || '[brak]';
}

export const getCronEditorMode = (cron) => {
    return parseQuartzCronToSimpleModel(cron) ? CronEditorMode.SIMPLE : CronEditorMode.ADVANCED;
}

export const validateQuartzCronForMinuteScheduler = (cron) => {
    const parts = splitQuartzCron(cron);
    if (!parts) {
        return 'Cron Quartz musi mieć 6 lub 7 segmentów';
    }
    const second = parts[0];
    if (!isSupportedSecondToken(second)) {
        return 'System obsługuje harmonogramy tylko z sekundą 0 lub *';
    }
    return null;
}

export const getQuartzCronEditorHint = (cron) => {
    const parts = splitQuartzCron(cron);
    if (!parts) return null;
    if (!parseQuartzCronToSimpleModel(cron)) {
        return 'Ten harmonogram korzysta z trybu zaawansowanego Quartz i nie da się go odwzorować w prostym kreatorze.';
    }
    if (parts.length === 7) {
        return 'Wykryto 7-segmentowy zapis Quartz. Przy zapisie z prostego kreatora zostanie on znormalizowany do 6 segmentów.';
    }
    if (parts[0] === '*') {
        return 'Sekunda * jest akceptowana, ale aplikacja i tak wykonuje harmonogram z dokładnością do pełnej minuty.';
    }
    return null;
}

